import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MypageLayout from "./MypageLayout";
import { emitToast } from "./Toast";
import TodoCard, { ACTION, ME } from "./TodoCard";
import { refreshBadgeCounts } from "./notifications";
import { jsonHeaders } from "./csrf";

// TODO（勤怠マイページ）。会議2026-07で確定したUI（第2波改修）。
// ・3カラムの区分表示を廃し、幅に応じて枚数が変わるカードグリッドで表示する
// ・並びは 未対応 → 対応中 → 完了。完了は初期表示しない（増え続けて取得が重くなるため）
// ・表示はステータスのチェックフィルタ（初期＝未対応・対応中）＋担当（自分/全員）で絞る
// ・状態はアクションボタンで示す：未対応=「対応する」/ 対応中=「完了」/ 完了=「完了取消」
// ・カード本体は共通コンポーネント TodoCard（ホームと共用）。この画面は一覧の状態管理と絞り込みを担う。
// データは /api/employee/todos/ から取得し、状態変更/編集/削除/メモも同APIへ反映する（demoServer に一元化）。

const ORDER = { open: 0, doing: 1, done: 2 };

const API_URL = process.env.REACT_APP_API_URL || "";

function TodoBoardPage() {
  const [todos, setTodos] = useState([]); // ホームと同じ /api/employee/todos/ から取得（demoServer に一元化）
  const [filters, setFilters] = useState({ open: true, doing: true, done: false }); // 初期＝未対応・対応中
  const [scope, setScope] = useState("mine"); // mine | all（既定=自分）
  const [expandedId, setExpandedId] = useState(null); // メモを開いているカード
  const [addTitle, setAddTitle] = useState("");
  const [addDue, setAddDue] = useState("");
  const [adding, setAdding] = useState(false); // ボタンの無効表示用（送信中はグレー化）
  const addingRef = useRef(false); // 二重作成の実ガード。ref は同期更新なので、再レンダ前の同フレーム連打でも再入を確実に弾く
  const [editingId, setEditingId] = useState(null); // 編集中のカード

  // TODO取得（ホームと同一API）。assignee/memos が無い簡易データにも備えて既定を補う。取得失敗は握りつぶす。
  // 表示中のステータスだけをサーバから取得する（既定＝未対応・対応中のみ／完了は初期取得しない）。
  // 完了は消えずに増え続けるため、常に全件取得すると開くたびに「これまで完了した全TODO」まで
  // 落として画面下の filter で捨てることになり、件数が増えるほど無駄に重くなる（ホームと同じ方針）。
  // 「完了」チェックを入れたときだけ完了も取得する（filters を依存にして自動で取り直す）。
  const refresh = useCallback(() => {
    const statuses = Object.keys(filters).filter((k) => filters[k]);
    if (statuses.length === 0) { setTodos([]); return; } // 何も選んでいなければ取得しない
    fetch(`${API_URL}/api/employee/todos/?status=${statuses.join(",")}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setTodos((d.results || []).map((t) => ({ assignee: null, memos: [], ...t }))); })
      .catch(() => { /* 取得失敗は無視 */ });
  }, [filters]);
  useEffect(() => { refresh(); }, [refresh]);

  // 変更系は BE に POST して再取得（サーバを唯一の真実にする）。
  const post = (path, payload) =>
    fetch(`${API_URL}${path}`, {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    });

  // 表示対象：ステータスのチェック＋担当（自分＝自分の担当＋担当者なし）。並びは 未対応→対応中→完了。
  const visible = useMemo(
    () => todos
      .filter((t) => filters[t.status] && (scope === "all" || t.assignee === ME || t.assignee == null))
      .slice()
      .sort((a, b) => ORDER[a.status] - ORDER[b.status]),
    [todos, filters, scope],
  );

  const toggleFilter = (k) => setFilters((p) => ({ ...p, [k]: !p[k] }));

  // アクション（対応する / 完了 / 完了取消）。未対応→対応中では担当者なしを自分が引き受ける（BE側で付与）。
  const advance = async (t) => {
    const a = ACTION[t.status];
    await post("/api/employee/todos/update/", { id: t.id, status: a.next });
    emitToast(a.msg);
    refresh();
    // 未対応(open)を抜けた/戻った＝TODOバッジ（状態由来）も増減する。
    refreshBadgeCounts();
  };

  // 編集（件名＋締切）。鉛筆で開始、保存で反映、キャンセル/Esc で破棄。入力の一時値は TodoCard 内で持つ。
  const startEdit = (t) => { setExpandedId(null); setEditingId(t.id); };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = async (id, title, due) => {
    await post("/api/employee/todos/update/", { id, title, due });
    setEditingId(null);
    refresh();
  };

  // 手動追加（未対応に積む・締め切り任意）
  const addManual = async () => {
    const title = addTitle.trim();
    // 送信中は無視：ボタン連打やEnter連打で同じTODOが2件作られるのを防ぐ
    // （入力欄のクリアは送信後なので、ガードが無いと2回目も中身が残ったまま通ってしまう）。
    if (!title || addingRef.current) return;
    addingRef.current = true;
    setAdding(true);
    try {
      await post("/api/employee/todos/", { title, due: addDue || null });
      setAddTitle("");
      setAddDue("");
      emitToast("TODOを追加しました");
      refresh();
      // 未対応TODOが1件増えた＝TODOバッジも増える。
      refreshBadgeCounts();
    } finally {
      addingRef.current = false;
      setAdding(false);
    }
  };

  // 削除（確認なしで即実行）。BE側が論理削除で行を残すため、誤操作は phpMyAdmin で復元できる。
  const doDelete = async (todo) => {
    const id = todo.id;
    await post("/api/employee/todos/delete/", { id });
    if (editingId === id) setEditingId(null);
    emitToast("削除しました");
    refresh();
    // 未対応TODOを削除した＝TODOバッジも減る。
    refreshBadgeCounts();
  };

  // メモ（カードクリックで開閉）
  const toggleMemo = (id) => setExpandedId((p) => (p === id ? null : id));
  const addMemo = async (id, text) => {
    const v = (text || "").trim();
    if (!v) return;
    await post("/api/employee/todos/memo/", { id, text: v });
    refresh();
  };
  // メモ削除（自分のメモのみ・×で即削除）。誤操作は起きにくい軽い操作のため確認なし。
  const deleteMemo = async (todoId, mid) => {
    await post("/api/employee/todos/memo/delete/", { id: todoId, mid });
    refresh();
  };

  return (
    <MypageLayout active="todo" title="TODO">
      {/* 手動追加（件名＋締め切り任意） */}
      <div className="mp-todo-add">
        <input
          className="mp-input mp-todo-add-title"
          placeholder="TODOを手動で追加"
          value={addTitle}
          onChange={(e) => setAddTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addManual(); }}
        />
        <input
          className="mp-input mp-todo-add-due"
          type="date"
          aria-label="締め切り（任意）"
          value={addDue}
          onChange={(e) => setAddDue(e.target.value)}
        />
        <button className="mp-btn mp-primary" onClick={addManual} disabled={adding}>追加</button>
      </div>

      {/* フィルタ：表示するステータス（既定＝未対応・対応中）＋担当（自分/全員） */}
      <div className="mp-todo-filters">
        <div className="mp-todo-checks">
          <span className="mp-todo-checklabel">表示</span>
          <label className="mp-todo-check"><input type="checkbox" checked={filters.open} onChange={() => toggleFilter("open")} /> 未対応</label>
          <label className="mp-todo-check"><input type="checkbox" checked={filters.doing} onChange={() => toggleFilter("doing")} /> 対応中</label>
          <label className="mp-todo-check"><input type="checkbox" checked={filters.done} onChange={() => toggleFilter("done")} /> 完了</label>
        </div>
        <div className="mp-tabs mp-todo-scope">
          <button className={`mp-tab${scope === "mine" ? " mp-on" : ""}`} onClick={() => setScope("mine")}>自分</button>
          <button className={`mp-tab${scope === "all" ? " mp-on" : ""}`} onClick={() => setScope("all")}>全員</button>
        </div>
      </div>

      {/* カードグリッド（幅に応じて枚数可変・未対応→対応中→完了の順） */}
      {visible.length === 0
        ? <div className="mp-todo-empty">表示するTODOはありません</div>
        : (
          <div className="mp-todo-grid">
            {visible.map((t) => (
              <TodoCard
                key={t.id}
                todo={t}
                isOpen={expandedId === t.id}
                isEditing={editingId === t.id}
                onToggleMemo={toggleMemo}
                onAdvance={advance}
                onStartEdit={startEdit}
                onCancelEdit={cancelEdit}
                onSaveEdit={saveEdit}
                onDelete={doDelete}
                onAddMemo={addMemo}
                onDeleteMemo={deleteMemo}
              />
            ))}
          </div>
        )}
    </MypageLayout>
  );
}

export default TodoBoardPage;
